// Code generated by apic; DO NOT EDIT.
import type { RestEndpointMeta } from "../types";
import type { SampleContext } from "./shared";
import { expandPath, isUnsafe, resolveAuthMode, hasMTLSLeaf } from "./shared";

// ── TypeScript-specific helper ────────────────────────────────────────────────

/**
 * Produce a TypeScript/JSON double-quoted string literal from a raw value.
 * JSON.stringify handles ", \, and control characters (F-4 appsec).
 */
export function tsStringLit(s: string): string {
  return JSON.stringify(s);
}

/**
 * tsSample returns a TypeScript code snippet for the given endpoint.
 *
 * When ep.methodName is available the snippet prefers the generated client:
 *   const client = createClient({ baseUrl, bearerToken })
 *   const result = await client.<methodName>(req)
 *
 * Falls back to a fetch() call when no methodName is present.
 *
 * Auth injection:
 *  - jwt / cookie → bearerToken passed to createClient
 *  - api_key      → note about apiKeySigner in ClientConfig
 *  - mtls/cac/piv → comment only (transport-layer TLS cert)
 *  - public       → no auth option
 */
export function tsSample(ep: RestEndpointMeta, ctx: SampleContext): string {
  const method = ep.method.toUpperCase();
  const expandedPath = expandPath(ep.path, ctx);
  const url = ctx.baseUrl.replace(/\/$/, "") + expandedPath;
  const methodName = ep.methodName;

  const lines: string[] = [];
  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);
  const unsafe = isUnsafe(method);

  if (methodName !== "") {
    // ── preferred path: generated client ──────────────────────────────────
    lines.push(`import { createClient } from "./client"; // generated client`);
    lines.push(``);
    lines.push(`async function main() {`);

    // Build createClient options.
    const clientOpts: string[] = [`baseUrl: ${tsStringLit(ctx.baseUrl)}`];

    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      clientOpts.push(`bearerToken: ${tsStringLit(tok)}`);
      if (ep.auth.csrf === "required" || ep.auth.csrf === "auto") {
        lines.push(`  // cookie+CSRF: also supply the X-CSRF-Token header on each request`);
      }
      if (composite && mtlsLeaf) {
        lines.push(`  // also requires mTLS client certificate (configure TLSClientConfig on the transport)`);
      }
    } else if (mode === "api_key") {
      // api_key uses a signer; note it in the config comment.
      lines.push(`  // api_key auth: supply an apiKeySigner in ClientConfig`);
      if (composite && mtlsLeaf) {
        lines.push(`  // also requires mTLS client certificate`);
      }
    } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the fetch transport`);
    }

    lines.push(`  const client = createClient({ ${clientOpts.join(", ")} });`);
    lines.push(``);

    // Build the request argument.
    if (unsafe && ctx.body !== undefined) {
      lines.push(`  const req = ${JSON.stringify(ctx.body)};`);
      lines.push(`  const result = await client.${methodName}(req);`);
    } else if (unsafe) {
      lines.push(`  const result = await client.${methodName}({});`);
    } else {
      lines.push(`  const result = await client.${methodName}({});`);
    }

    lines.push(`  console.log(result);`);
    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  } else {
    // ── fallback: raw fetch ──────────────────────────────────────────────
    lines.push(`async function main() {`);

    const headers: Record<string, string> = { Accept: "application/json" };

    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      headers["Authorization"] = `Bearer ${tok}`;
      if (ep.auth.csrf === "required" || ep.auth.csrf === "auto") {
        headers["X-CSRF-Token"] = "<csrf-token>";
      }
    } else if (mode === "api_key") {
      const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
      headers["X-API-Key"] = key;
    }

    if (unsafe) {
      headers["Content-Type"] = "application/json";
    }

    lines.push(`  const headers: Record<string, string> = ${JSON.stringify(headers, null, 4).replace(/\n/g, "\n  ")};`);

    if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the fetch transport`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`  // also requires mTLS client certificate`);
    }

    lines.push(``);

    if (unsafe) {
      const body = ctx.body !== undefined ? JSON.stringify(ctx.body) : "{}";
      lines.push(`  const resp = await fetch(${tsStringLit(url)}, {`);
      lines.push(`    method: "${method}",`);
      lines.push(`    headers,`);
      lines.push(`    body: JSON.stringify(${body}),`);
      lines.push(`  });`);
    } else {
      lines.push(`  const resp = await fetch(${tsStringLit(url)}, { method: "${method}", headers });`);
    }

    lines.push(`  const data: unknown = await resp.json();`);
    lines.push(`  console.log(data);`);
    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  }

  return lines.join("\n");
}
